// src/components/vault/documents/id-validation/IDDataEditForm.tsx

import React, { useEffect, useState } from "react";
import {
  User,
  MapPin,
  Save,
  X,
  Loader2,
  LockKeyhole,
  Mail,
} from "lucide-react";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { MEXICAN_STATES } from "./constants";
import type { IDDataEditFormProps, ExtractedINEData } from "./types";
import {
  isSecondSurnameNotProvided,
  SECOND_SURNAME_NOT_PROVIDED,
  validateIDData,
} from "./idDataValidation";
import { NipDialCodeSelect } from "./NipDialCodeSelect";

const EMPTY_FIELD_ERRORS: Record<string, string> = {};

export const IDDataEditForm: React.FC<IDDataEditFormProps> = ({
  data,
  onSave,
  onCancel,
  allowCancel = true,
  isSubmitting = false,
  requiresBdcAuthorization = false,
  strictBdcValidation = false,
  serverFieldErrors = EMPTY_FIELD_ERRORS,
  requireBirthDate = false,
  contactReadOnly = false,
}) => {
  const [formData, setFormData] = useState<ExtractedINEData>(() => ({
    ...data,
    apellidoMaterno: isSecondSurnameNotProvided(data.apellidoMaterno)
      ? ""
      : data.apellidoMaterno,
  }));
  const [noSecondSurname, setNoSecondSurname] = useState(() =>
    isSecondSurnameNotProvided(data.apellidoMaterno),
  );
  const [fieldErrors, setFieldErrors] = useState(serverFieldErrors);

  useEffect(() => {
    setFieldErrors(serverFieldErrors);
  }, [serverFieldErrors]);

  const validate = (submittedData: ExtractedINEData): boolean => {
    const errors = validateIDData(submittedData, {
      requireDialCode: requiresBdcAuthorization,
      requireStrictCurp: requiresBdcAuthorization,
      requireExteriorNumber: true,
      requireBirthDate,
      strictBdcValidation,
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof ExtractedINEData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    const errorKeys =
      field === "nombre" || field === "segundoNombre"
        ? ["nombre", "segundoNombre"]
        : [field];
    if (errorKeys.some((key) => fieldErrors[key])) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        errorKeys.forEach((key) => delete next[key]);
        return next;
      });
    }
  };

  const handleAddressChange = (
    field: keyof ExtractedINEData["direccion"],
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      direccion: {
        ...prev.direccion,
        [field]: value,
      },
    }));
    const key = `direccion.${field}`;
    if (fieldErrors[key]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const submittedData = {
      ...formData,
      apellidoMaterno: noSecondSurname
        ? SECOND_SURNAME_NOT_PROVIDED
        : formData.apellidoMaterno,
    };
    if (!validate(submittedData)) return;
    onSave(submittedData);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="py-6 px-4 sm:px-6"
      aria-busy={isSubmitting}
      noValidate
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          Editar datos
        </h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          Corrige los datos que esten incorrectos y guarda los cambios.
        </p>
      </div>

      {/* Form sections */}
      <div className="space-y-6 max-w-lg mx-auto">
        {/* Personal info section */}
        <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">
              Datos personales
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nombre" className="text-xs text-slate-600">
                Primer nombre
              </Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => handleInputChange("nombre", e.target.value)}
                placeholder="Primer nombre"
                maxLength={strictBdcValidation ? 26 : undefined}
                aria-invalid={Boolean(fieldErrors.nombre)}
                className={`h-10 ${fieldErrors.nombre ? "border-red-500" : ""}`}
              />
              {fieldErrors.nombre && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldErrors.nombre}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="segundoNombre" className="text-xs text-slate-600">
                Segundo nombre (opcional)
              </Label>
              <Input
                id="segundoNombre"
                value={formData.segundoNombre}
                onChange={(e) =>
                  handleInputChange("segundoNombre", e.target.value)
                }
                placeholder="Segundo nombre"
                maxLength={strictBdcValidation ? 26 : undefined}
                aria-invalid={Boolean(fieldErrors.segundoNombre)}
                className={`h-10 ${fieldErrors.segundoNombre ? "border-red-500" : ""}`}
              />
              {fieldErrors.segundoNombre && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldErrors.segundoNombre}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="apellidoPaterno"
                className="text-xs text-slate-600"
              >
                Apellido Paterno
              </Label>
              <Input
                id="apellidoPaterno"
                value={formData.apellidoPaterno}
                onChange={(e) =>
                  handleInputChange("apellidoPaterno", e.target.value)
                }
                placeholder="Apellido paterno"
                maxLength={strictBdcValidation ? 26 : undefined}
                className={`h-10 ${fieldErrors.apellidoPaterno ? "border-red-500" : ""}`}
              />
              {fieldErrors.apellidoPaterno && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldErrors.apellidoPaterno}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="apellidoMaterno"
                className="text-xs text-slate-600"
              >
                Apellido Materno
              </Label>
              <Input
                id="apellidoMaterno"
                value={noSecondSurname ? "" : formData.apellidoMaterno}
                onChange={(e) =>
                  handleInputChange("apellidoMaterno", e.target.value)
                }
                disabled={noSecondSurname || isSubmitting}
                maxLength={strictBdcValidation ? 26 : undefined}
                aria-invalid={Boolean(fieldErrors.apellidoMaterno)}
                aria-describedby={
                  fieldErrors.apellidoMaterno
                    ? "apellido-materno-error"
                    : undefined
                }
                placeholder={
                  noSecondSurname ? "Sin segundo apellido" : "Apellido materno"
                }
                className={`h-10 disabled:bg-slate-100 disabled:opacity-100 disabled:placeholder:text-slate-500 ${fieldErrors.apellidoMaterno ? "border-red-500" : ""}`}
              />
              {fieldErrors.apellidoMaterno && (
                <p
                  id="apellido-materno-error"
                  className="text-xs text-red-500 mt-1"
                >
                  {fieldErrors.apellidoMaterno}
                </p>
              )}
              <Label
                htmlFor="no-second-surname"
                className={`flex min-h-6 items-center gap-2 text-xs font-normal leading-4 text-slate-600 ${isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
              >
                <Checkbox
                  id="no-second-surname"
                  className="shadow-none [&_svg]:size-3"
                  checked={noSecondSurname}
                  disabled={isSubmitting}
                  onCheckedChange={(checked) => {
                    setNoSecondSurname(checked === true);
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next.apellidoMaterno;
                      return next;
                    });
                  }}
                />
                <span>No tengo segundo apellido</span>
              </Label>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="curp" className="text-xs text-slate-600">
                CURP
              </Label>
              <Input
                id="curp"
                value={formData.curp}
                onChange={(e) =>
                  handleInputChange("curp", e.target.value.toUpperCase())
                }
                placeholder="CURP"
                className={`h-10 font-mono ${fieldErrors.curp ? "border-red-500" : ""}`}
                maxLength={18}
              />
              {fieldErrors.curp && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.curp}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rfc" className="text-xs text-slate-600">
                RFC
              </Label>
              <Input
                id="rfc"
                value={formData.rfc}
                onChange={(e) =>
                  handleInputChange("rfc", e.target.value.toUpperCase())
                }
                placeholder="RFC"
                required={strictBdcValidation && requireBirthDate}
                aria-invalid={Boolean(fieldErrors.rfc)}
                className={`h-10 font-mono ${fieldErrors.rfc ? "border-red-500" : ""}`}
                maxLength={13}
              />
              {fieldErrors.rfc && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.rfc}</p>
              )}
            </div>
          </div>
          {requiresBdcAuthorization && (
            <div className="space-y-2 mt-4">
              <Label htmlFor="fechaNacimiento" className="text-xs text-slate-600">
                Fecha de nacimiento
              </Label>
              <Input
                id="fechaNacimiento"
                required={requireBirthDate}
                aria-invalid={Boolean(fieldErrors.fechaNacimiento)}
                value={formData.fechaNacimiento}
                onChange={(e) =>
                  handleInputChange("fechaNacimiento", e.target.value)
                }
                placeholder="AAAA-MM-DD"
                className={`h-10 ${fieldErrors.fechaNacimiento ? "border-red-500" : ""}`}
              />
              <p className="text-xs text-slate-500">Usa el formato AAAA-MM-DD.</p>
              {fieldErrors.fechaNacimiento && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldErrors.fechaNacimiento}
                </p>
              )}
            </div>
          )}
          <div className="mt-4">
            <Label className="text-xs text-slate-600">Genero</Label>
            <div
              className={`flex gap-6 h-10 items-center mt-1 ${fieldErrors.genero ? "rounded-md border border-red-500 px-3" : ""}`}
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="genero"
                  value="Masculino"
                  checked={formData.genero === "Masculino"}
                  onChange={(e) => handleInputChange("genero", e.target.value)}
                  className="w-4 h-4 text-[#28397a]"
                />
                <span className="text-sm text-slate-700">Masculino</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="genero"
                  value="Femenino"
                  checked={formData.genero === "Femenino"}
                  onChange={(e) => handleInputChange("genero", e.target.value)}
                  className="w-4 h-4 text-[#28397a]"
                />
                <span className="text-sm text-slate-700">Femenino</span>
              </label>
            </div>
            {fieldErrors.genero && (
              <p className="text-xs text-red-500 mt-1">{fieldErrors.genero}</p>
            )}
          </div>
        </div>

        {/* Address section */}
        <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">Domicilio</h4>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="calle" className="text-xs text-slate-600">
                  Calle
                </Label>
                <Input
                  id="calle"
                  value={formData.direccion.calle}
                  onChange={(e) => handleAddressChange("calle", e.target.value)}
                  placeholder="Calle"
                  maxLength={strictBdcValidation && requireBirthDate ? 80 : undefined}
                  className={`h-10 ${fieldErrors["direccion.calle"] ? "border-red-500" : ""}`}
                />
                {fieldErrors["direccion.calle"] && (
                  <p className="text-xs text-red-500 mt-1">
                    {fieldErrors["direccion.calle"]}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="numeroExterior"
                  className="text-xs text-slate-600"
                >
                  No. Exterior
                </Label>
                <Input
                  id="numeroExterior"
                  value={formData.direccion.numeroExterior}
                  onChange={(e) =>
                    handleAddressChange("numeroExterior", e.target.value)
                  }
                  placeholder="No. Ext."
                  maxLength={strictBdcValidation && requireBirthDate ? 10 : undefined}
                  className={`h-10 ${fieldErrors["direccion.numeroExterior"] ? "border-red-500" : ""}`}
                />
                {fieldErrors["direccion.numeroExterior"] && (
                  <p className="text-xs text-red-500 mt-1">
                    {fieldErrors["direccion.numeroExterior"]}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="numeroInterior"
                  className="text-xs text-slate-600"
                >
                  No. Interior (opcional)
                </Label>
                <Input
                  id="numeroInterior"
                  value={formData.direccion.numeroInterior}
                  onChange={(e) =>
                    handleAddressChange("numeroInterior", e.target.value)
                  }
                  placeholder="No. Int."
                  maxLength={strictBdcValidation && requireBirthDate ? 10 : undefined}
                  aria-invalid={Boolean(fieldErrors["direccion.numeroInterior"])}
                  className={`h-10 ${fieldErrors["direccion.numeroInterior"] ? "border-red-500" : ""}`}
                />
                {fieldErrors["direccion.numeroInterior"] && (
                  <p className="text-xs text-red-500 mt-1">
                    {fieldErrors["direccion.numeroInterior"]}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="colonia" className="text-xs text-slate-600">
                  Colonia
                </Label>
                <Input
                  id="colonia"
                  value={formData.direccion.colonia}
                  onChange={(e) =>
                    handleAddressChange("colonia", e.target.value)
                  }
                  placeholder="Colonia"
                  maxLength={strictBdcValidation && requireBirthDate ? 65 : undefined}
                  className={`h-10 ${fieldErrors["direccion.colonia"] ? "border-red-500" : ""}`}
                />
                {fieldErrors["direccion.colonia"] && (
                  <p className="text-xs text-red-500 mt-1">
                    {fieldErrors["direccion.colonia"]}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="codigoPostal"
                  className="text-xs text-slate-600"
                >
                  Codigo Postal
                </Label>
                <Input
                  id="codigoPostal"
                  value={formData.direccion.codigoPostal}
                  onChange={(e) =>
                    handleAddressChange("codigoPostal", e.target.value)
                  }
                  placeholder="C.P."
                  className={`h-10 ${fieldErrors["direccion.codigoPostal"] ? "border-red-500" : ""}`}
                  maxLength={5}
                />
                {fieldErrors["direccion.codigoPostal"] && (
                  <p className="text-xs text-red-500 mt-1">
                    {fieldErrors["direccion.codigoPostal"]}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="municipio" className="text-xs text-slate-600">
                  Municipio/Alcaldia
                </Label>
                <Input
                  id="municipio"
                  value={formData.direccion.municipio}
                  onChange={(e) =>
                    handleAddressChange("municipio", e.target.value)
                  }
                  placeholder="Municipio"
                  maxLength={strictBdcValidation && requireBirthDate ? 65 : undefined}
                  className={`h-10 ${fieldErrors["direccion.municipio"] ? "border-red-500" : ""}`}
                />
                {fieldErrors["direccion.municipio"] && (
                  <p className="text-xs text-red-500 mt-1">
                    {fieldErrors["direccion.municipio"]}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="estado" className="text-xs text-slate-600">
                  Estado
                </Label>
                <Select
                  value={formData.direccion.estado}
                  onValueChange={(value) =>
                    handleAddressChange("estado", value)
                  }
                >
                  <SelectTrigger
                    className={`h-10 ${fieldErrors["direccion.estado"] ? "border-red-500" : ""}`}
                  >
                    <SelectValue placeholder="Selecciona estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.direccion.estado &&
                      !MEXICAN_STATES.includes(formData.direccion.estado) && (
                        <SelectItem value={formData.direccion.estado}>
                          {formData.direccion.estado}
                        </SelectItem>
                      )}
                    {MEXICAN_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldErrors["direccion.estado"] && (
                  <p className="text-xs text-red-500 mt-1">
                    {fieldErrors["direccion.estado"]}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {requiresBdcAuthorization && (
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">
              Datos de contacto
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3 space-y-2">
              <Label htmlFor="email" className="text-xs text-slate-600">
                Correo electrónico
              </Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  readOnly
                  aria-readonly="true"
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby="email-readonly-note"
                  className={`h-11 cursor-text rounded-lg bg-slate-100/80 pr-10 font-medium text-slate-600 focus:border-slate-300 ${
                    fieldErrors.email ? "border-red-500" : "border-slate-200"
                  }`}
                  maxLength={100}
                />
                <LockKeyhole
                  aria-hidden="true"
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                />
              </div>
              <p
                id="email-readonly-note"
                className="text-xs leading-relaxed text-slate-500"
              >
                Correo vinculado a la invitación. No se puede modificar.
              </p>
              {fieldErrors.email && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dial_code" className="text-xs text-slate-600">
                País / lada
              </Label>
              <NipDialCodeSelect
                id="dial_code"
                disabled={contactReadOnly}
                value={formData.dial_code}
                onValueChange={(value) => handleInputChange("dial_code", value)}
                invalid={Boolean(fieldErrors.dial_code)}
                describedBy={
                  fieldErrors.dial_code ? "dial-code-error" : undefined
                }
              />
              {fieldErrors.dial_code && (
                <p id="dial-code-error" className="text-xs text-red-500 mt-1">
                  {fieldErrors.dial_code}
                </p>
              )}
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="phone_number" className="text-xs text-slate-600">
                Número de teléfono
              </Label>
              <div className="relative">
                <Input
                  id="phone_number"
                  type="tel"
                  inputMode="numeric"
                  value={formData.phone_number}
                  readOnly
                  aria-readonly="true"
                  className={`h-11 cursor-text rounded-lg bg-slate-100/80 pr-10 font-medium text-slate-600 focus:border-slate-300 ${fieldErrors.phone_number ? "border-red-500" : "border-slate-200"}`}
                  maxLength={10}
                />
                <LockKeyhole
                  aria-hidden="true"
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                />
              </div>
              <p className="text-xs leading-relaxed text-slate-500">
                Teléfono vinculado a la invitación. No se puede modificar.
              </p>
              {fieldErrors.phone_number && (
                <p className="text-xs text-red-500 mt-1">
                  {fieldErrors.phone_number}
                </p>
              )}
            </div>
          </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto mt-8">
        {allowCancel && (
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-12 gap-2 text-base border-slate-200 hover:bg-slate-50"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <X className="w-4 h-4 text-slate-500" />
            <span>Cancelar</span>
          </Button>
        )}

        <Button
          type="submit"
          className="flex-1 h-12 gap-2 text-base bg-[#28397a] hover:bg-[#1e2d5f]"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span>{isSubmitting ? "Guardando..." : "Guardar cambios"}</span>
        </Button>
      </div>
    </form>
  );
};
